import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { setRegistrationDescription, setRegistrationHeader, setRegistrationImage, setRegistrationLogo, toggleCustomizeFormModal, updateRegistrationSettings } from '../../../../../../store/actions/admin/create-event';
import { getRegistrationQuestions } from '../../../../../../store/actions/admin/registration-questions';
import { EventsState } from '../../../../../../store/types';
import { ColorKeys, EPreviewTypes, QuestionType, RegistrationQuestion, RegistrationStep, RegistrationStepType, RegistrationTypes, TextOverrides, TicketingSet } from '../../../../../../types/working-model';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import TextInput from '../../../../../general-ui/text-input/text';
import Textarea from '../../../../../general-ui/textarea/textarea';
import Modal from '../../../../../general-ui/modal/modal';
import formLeft from '../../../../../../images/form-left.svg';
import formRight from '../../../../../../images/form-right.svg';
import RegistrationStepsHeader from './registration-steps-header';
import { SingleSignOnIcon } from '../../navigation/panel/settings/registration-settings/single-sign-on';
import { getStartTime } from '../../../../../../utils/get-event-start-time';
import { EContentColorOverrideNames } from '../../../session/editor/moduleControls/edit-section-colors-modal';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import EditableTextDraft from '../../../../../general-ui/editable-text/editable-text-draft';
import { getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import SelectNative from '../../../../../general-ui/select/select-native';
import CustomCheckbox from '../../../../../general-ui/checkbox/custom-checkbox';
import { TemplateClassNames } from '../../../../../../types/template-layouts';
import { Button } from '../../../../../general-ui/button/button';
import { EIcon } from '../../../../../general-ui/icon/icon';
import { AvatarContent } from '../../../../../live-event/registration/registration';
import RegistrationPreviewModal from '../preview/registration-preview';
import RegistrationQuestionsModal from './registration-questions-modal';
import { getAsset } from '../../../../../live-event/modules/assets/asset-map';
import useTranslationModules from '../../../../../../i18n/useTranslationModules';
import { ParamsProps } from '../../../../../live-event/live-event';
import { getHomepageDefaultLanguage } from '../../../../../../utils/get-language';
import { UploadImageModal } from '../../../../../general-ui/image-upload-modal/image-upload-modal';
import { SelectOption } from '../../../../../general-ui/select/select';
import { useFieldRequired, useSetRequiredField } from '../../../../../../utils/registration-required-fields';
import './registration.scss';

function arrayToOptions(options: string[]): SelectOption[] {
	return options.map((option: string) => ({ label: option, value: option }));
}

export default function RegistrationEditor(): JSX.Element {
	const dispatch = useDispatch();
	const {
		CreateEventReducer: {
			workingEvent,
			registrationStep,
			editorSize,
			previewMode,
			loadingEvent,
		},
		AuthReducer: {
			user,
			token
		},
		EventPanelReducer: {
			panelOpen,
		},
	} = useSelector((state: EventsState) => state);
	const registrationSteps = workingEvent?.registration_steps;
	const registration_settings = workingEvent?.registration_settings;
	const eventStartDate = workingEvent ? String(getStartTime(workingEvent!, true)) : null;
	const eventStartMonthDay = eventStartDate?.split(",")[0].split(" ");
	const [logo, setLogo] = useState<string | undefined>(workingEvent?.registration_settings?.logo);
	const [largeImage, setLargeImage] = useState<string | undefined>(workingEvent?.registration_settings?.image);
	const [openImageModal, setOpenImageModal] = useState<string | null>(null);
	const [layoutOpen, setLayoutOpen] = useState(false);

	const { language } = useParams() as ParamsProps;
	const baseLanguage = getHomepageDefaultLanguage();

	useTranslationModules({ language: language || "en" });

	const { t } = useTranslation("homepage");

	// monitoring registration changes to ensure fields are on and required when needed for other registration components
	const isFieldRequired = useFieldRequired();
	useSetRequiredField(isFieldRequired);

	const registrationStyle = workingEvent?.registration_settings?.style;
	const layout = registrationStyle?.layout || "form-right";
	const [_layout, set_Layout] = useState(layout);
	const styling_overrides = registrationStyle?.styling_overrides;
	const titleOverrides = styling_overrides?.content?.title;
	const descriptionOverrides = styling_overrides?.content?.description;
	const template = TemplateClassNames[workingEvent?.template.name ?? 'Studio 54'];
	const singleSignOns = workingEvent?.registration_settings?.singleSignOns;

	const activeSteps = registrationSteps?.filter(step => step.isOn);
	const ticketingStep = registrationSteps?.find(step => step.type == RegistrationStepType.ticketing && step.isOn);

	const { stepNumber, isFinalStep, notSkippable } = useMemo(() => {
		const stepNumber = activeSteps
			? activeSteps?.findIndex(step => step.type === registrationStep) + 1
			: undefined;
		const isFinalStep = stepNumber === activeSteps?.length;
		const notSkippable = registrationStep === RegistrationStepType.ticketing;
		return { stepNumber, isFinalStep, notSkippable };
	}, [activeSteps, registrationStep]);

	const ACTIVE_STEPS_MAP = activeSteps?.reduce((acc, curr) => {
		acc[curr.type] = curr;
		return acc;
	}, {} as { [key: string]: RegistrationStep; });

	useEffect(() => {
		dispatch(getRegistrationQuestions(user!.active_channel, token!));
	}, [dispatch, token, user]);


	function openModal() {
		dispatch(toggleCustomizeFormModal(true));
	}

	function setModuleTitle(text: string) {
		dispatch(
			setRegistrationHeader(
				updateTranslateKey({
					translateString: registration_settings!.title ?? { base: text, changed: 'true' },
					input: text,
					baseLanguage,
					language
				})
			)
		);
	}

	function setModuleDescription(text: string) {
		dispatch(
			setRegistrationDescription(
				updateTranslateKey({
					translateString: registration_settings!.description ?? { base: text, changed: 'true' },
					input: text,
					baseLanguage,
					language
				})
			)
		);
	}

	const saveLogo = useCallback((image: { original: string, edited: string; }) => {
		// TODO: refactor to keep original image for future editing
		dispatch(setRegistrationLogo(image.edited));
		setLogo(image.edited);
	}, [dispatch]);

	const saveLargeImage = useCallback((image: { original: string, edited: string; }) => {
		// TODO: refactor to keep original image for future editing
		dispatch(setRegistrationImage(image.edited));
		setLargeImage(image.edited);
	}, [dispatch]);

	function handleTitleOverrides(overrides: TextOverrides) {
		if (!workingEvent?.registration_settings) return;
		const updatedOverrides = {
			...styling_overrides,
			content: {
				...styling_overrides?.content,
				title: overrides
			}
		};
		const updatedSettings = {
			...workingEvent.registration_settings,
			style: {
				...registrationStyle,
				styling_overrides: updatedOverrides
			}
		};
		dispatch(updateRegistrationSettings(token!, workingEvent!.uuid, updatedSettings));
	}
	function handleDescriptioOverrides(overrides: TextOverrides) {
		if (!workingEvent?.registration_settings) return;
		const updatedOverrides = {
			...styling_overrides,
			content: {
				...styling_overrides?.content,
				description: overrides
			}
		};
		const updatedSettings = {
			...workingEvent.registration_settings,
			style: {
				...registrationStyle,
				styling_overrides: updatedOverrides
			}
		};
		dispatch(updateRegistrationSettings(token!, workingEvent!.uuid, updatedSettings));
	}
	function handleSaveRegistrationLayout() {
		if (!workingEvent?.registration_settings) return;
		const updatedSettings = {
			...workingEvent.registration_settings,
			style: {
				...registrationStyle,
				layout: _layout
			}
		};
		dispatch(updateRegistrationSettings(token!, workingEvent!.uuid, updatedSettings));
		setLayoutOpen(false);
	}

	const renderQuestion = useCallback((question: RegistrationQuestion, index: number) => {
		const isRequired = workingEvent?.required_registration_questions?.includes(question.registration_question);
		switch (question.type) {
			case QuestionType.email:
			case QuestionType.numeric:
			case QuestionType.text: {
				// TODO
				// once we add Gate types, we should query on that instead of the name
				if (question.name === 'Passcode' && workingEvent?.registration_settings?.type !== RegistrationTypes.gated) return null;
				return <TextInput
					id={`question-text-${index}`}
					key={question.registration_question}
					onChange={() => ({})}
					required={isRequired}
					email={question.type === QuestionType.email}
					numeric={question.type === QuestionType.numeric}
					// below is correct, I just can't convince typescript that it is, hence the any
					label={question.global ? t(`registration.registration_fields.${question.registration_question}` as any) : question.name}
					placeholder={question.global ? t(`registration.registration_fields.${question.registration_question}` as any) : question.name}
				/>;
			}
			case QuestionType.select: {
				return <SelectNative
					onChange={() => ({})}
					options={arrayToOptions(question.options || [])}
					label={question.name}
					required={isRequired}
					name={question.name}
				/>;
			}
			case QuestionType.textarea: {
				return <Textarea
					id={`question-textarea-${index}`}
					onChange={() => ({})}
					required={isRequired}
					key={question.registration_question}
					label={question.global ? t(`registration.registration_fields.${question.registration_question}` as any) : question.name}
					placeholder={question.global ? t(`registration.registration_fields.${question.registration_question}` as any) : question.name}
				/>;
			}
			case QuestionType.checkbox: {
				return (
					<CustomCheckbox
						key={question.registration_question}
						label={question.name}
						checked={question.default_value === 'checked'}
						onChange={() => ({})}
						required={isRequired}
					/>
				);
			}
			default: return <div key={question.registration_question}>No option</div>;
		}
	}, [t, workingEvent?.required_registration_questions, workingEvent?.registration_settings?.type]);

	const [selectedTicket, setSelectedTicket] = useState<TicketingSet>();

	const renderTickets = useMemo(() => {
		if (ticketingStep?.ticketing) {
			return ticketingStep?.ticketing.map((ticket: TicketingSet) => (
				<div key={`ticket-${ticket.uuid}`} className="ticket">
					<div className="ticket-info">
						<p className="ticket-name">{ticket.name}</p>
						<p className="ticket-description">{ticket.description}</p>
						<p className="ticket-price">${ticket.price.toFixed(2)}</p>
					</div>
					<div className="ticket-checkbox">
						<CustomCheckbox
							name={`ticket-${ticket.uuid}`}
							size={26}
							onChange={() => setSelectedTicket(ticket)}
							checked={ticket.uuid == selectedTicket?.uuid}
							selectedIconName={ICONS.SQUARE_CHECKBOX_SELECTED}
						/>
					</div>
				</div>
			));
		} else return <></>;
	}, [ticketingStep?.ticketing, selectedTicket]);

	const renderForm = useCallback((type: RegistrationStepType) => {
		if (type !== RegistrationStepType.general && type !== RegistrationStepType.profile) return <></>;
		return (
			<>
				<form onSubmit={(e: any) => e.preventDefault()}>
					<button className="round" onClick={openModal}>
						<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={10} />
					</button>
					{ACTIVE_STEPS_MAP?.[type]?.questions?.map((question, index) => {
						return (<div key={index} className={classNames('evt-field-wrapper', template)}>
							{renderQuestion(question, index)}
						</div>);
					})}
				</form>
				{
					type === RegistrationStepType.general && ticketingStep?.ticketing && ticketingStep.ticketing.length > 0 && (
						<div className={classNames('evt-field-wrapper', template)}>
							<div className="ticketing">{renderTickets}</div>
						</div>
					)
				}
			</>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ACTIVE_STEPS_MAP, renderQuestion, template, selectedTicket]);

	const formContent = useMemo(() => {
		switch (registrationStep) {
			case RegistrationStepType.ticketing: return (
				<div className="ticketing">
					<h2>{t("registration.Checkout")}</h2>
					<div className="credit-card">
						<h2>{selectedTicket ? `${selectedTicket?.name} $${selectedTicket?.price.toFixed(2)}` : ``}</h2>
						<div className={classNames('evt-field-wrapper', template)}>
							<TextInput label="Name on Card" id="card_name" name="card_name" required />
						</div>
						<div className={classNames('evt-field-wrapper', template)}>
							<TextInput label="Credit Card" id="card" name="card" required />
						</div>
						<div className="small-cols">
							<div className={classNames('evt-field-wrapper', template)}>
								<TextInput label="Expiration" required />
							</div>
							<div className={classNames('evt-field-wrapper', template)}>
								<TextInput label="CVC" required />
							</div>
							<div className={classNames('evt-field-wrapper', template)}>
								<TextInput label="Zip Code" id="card_zip" name="card_zip" required />
							</div>
						</div>
					</div>
				</div>
			);
			case RegistrationStepType.avatar: return (
				<>
					<AvatarContent
						footerProps={{
							isFirstStep: stepNumber === 1,
							isFinalStep,
							onPrevStepClick: () => null,
							singleSignOns: singleSignOns || [],
							waiting: false,
							error: undefined
						}}
						template={workingEvent!.template.name}
						onSaveData={() => null}
						profile={null}
						channel={0}
						fields={[]}
						defaultValues={{}}
						isDisplayOnly
					/>
				</>
			);
			case RegistrationStepType.profile: return (
				<>
					<h4>{t("registration.Tell us about yourself")}</h4>
					{renderForm(RegistrationStepType.profile)}
				</>
			);
			case RegistrationStepType.general: return (
				<>
					<h4>{t("registration.Register for the event")}</h4>
					{renderForm(RegistrationStepType.general)}
				</>
			);
			default: return <></>;
		}
	}, [registrationStep, renderForm, isFinalStep, stepNumber, singleSignOns, workingEvent, template, t, selectedTicket]);

	const buttonGroup = useMemo(() => {
		if (stepNumber === 1) return (
			<div className="registration-actions primary">
				<Button
					classButton="primary full-width"
					template={template}
					typeBtn="submit"
				>
					{t("Next")}
				</Button>
				<div className="button-icon-wrapper">
					{singleSignOns?.map((signOn) => {
						return (
							<Button
								key={signOn}
								isRound={true}
								classButton={classNames('secondary', signOn)}
								template={template}
								typeBtn="button"
								aria-label={signOn}
							>
								<SingleSignOnIcon type={signOn} />
							</Button>
						);
					})}
				</div>
			</div>
		);
		else if (isFinalStep) return (
			<div className="registration-actions secondary">
				<Button
					isRound={true}
					classButton={classNames('registration-button secondary')}
					template={template}
					typeBtn="button"
				>
					<EIcon name="keyboard-arrow-left" />
				</Button>

				<div>
					{!notSkippable && (<Button
						classButton={classNames('registration-button secondary')}
						template={template}
						typeBtn="submit"
					>
						{t("Skip")}
					</Button>)}
					<Button
						classButton={classNames('registration-button primary')}
						template={template}
						typeBtn="submit"
					>
						{t("Submit")}
					</Button>
				</div>
			</div>
		);
		else return (
			<div className="registration-actions secondary">
				<Button
					isRound={true}
					classButton={classNames('registration-button secondary')}
					template={template}
				>
					<EIcon name="keyboard-arrow-left" />
				</Button>
				<div>
					{!notSkippable && (<Button
						classButton={classNames('registration-button secondary')}
						template={template}
						typeBtn="submit"
					>
						{t("Skip")}
					</Button>)}
					<Button
						classButton={classNames('registration-button primary')}
						template={template}
						typeBtn="submit"
					>
						{t("Next")}
					</Button>
				</div>
			</div>
		);
	}, [stepNumber, isFinalStep, notSkippable, singleSignOns, t, template]);

	const closeRegLayout = useCallback(() => {
		setLayoutOpen(false);
	}, []);

	const { title, description } = getTextValue(registration_settings, language);

	return (
		<div className="editor-main-border">
			{workingEvent?.registration_on && <>
				<RegistrationStepsHeader />
				<div className={classNames("editor registration", layout, editorSize, template, { 'panel-open': !!panelOpen, loading: loadingEvent })}>
					<div className="editor-left" style={{ backgroundImage: `url(${workingEvent?.registration_settings?.image ?? getAsset(template, 'registration_header')})` }}>
						<div className="title-container">
							{eventStartDate && eventStartDate !== "null"//the get startDate func sometimes returns null as a string
								? (
									<div className="date-container">
										<div className="calendar-card">
											<p>{eventStartMonthDay?.[0]}</p>
											<h3>{eventStartMonthDay?.[1]}</h3>
										</div>
										{eventStartDate}
									</div>
								)
								: null
							}
							<EditableTextSlim
								tagType="h1"
								elementName={EContentColorOverrideNames.title}
								value={title || workingEvent?.name}
								onChange={setModuleTitle}
								colorOverride={titleOverrides?.color}
								defaultColor={ColorKeys.bodyTextColor}
								onOverrideChange={handleTitleOverrides}
								disabled={loadingEvent}
							/>
							<EditableTextDraft
								elementName={EContentColorOverrideNames.description}
								value={description || "Enter Description"}
								onChange={setModuleDescription}
								onOverrideChange={handleDescriptioOverrides}
								colorOverride={descriptionOverrides?.color}
								defaultColor={ColorKeys.bodyTextColor}
								disabled={loadingEvent}
							/>
						</div>
						<div className="edit-layout-buttons">
							<button className="round" onClick={() => setOpenImageModal("Image")}>
								<Icon name={ICONS.PAINTBRUSH} size={14} color={COLORS.WHITE} />
							</button>
							<button className="round" onClick={() => setLayoutOpen(true)}>
								<Icon name={ICONS.EDIT} size={14} color={COLORS.WHITE} />
							</button>
						</div>
					</div>
					<div className={classNames("editor-form", layout)}>
						<div className="registration-logo">
							{logo ? (
								<img
									className="logo"
									alt="Logo"
									onClick={() => setOpenImageModal("Logo")}
									src={logo}
								/>
							) : (
								<h3 onClick={() => setOpenImageModal("Logo")}>
									{"Your Logo"}
								</h3>
							)}
							<button onClick={() => setOpenImageModal("Logo")} className="round edit-button">
								<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={12} />
							</button>
						</div>
						<div className="form-card">
							{formContent}
							{buttonGroup}
						</div>
					</div>
					<UploadImageModal
						originalImage={openImageModal === "Logo" ? logo : largeImage}
						fileTitle={openImageModal || ""}
						open={openImageModal ? true : false}
						close={() => setOpenImageModal(null)}
						onSave={openImageModal === "Logo" ? saveLogo : saveLargeImage}
					/>
					<RegistrationQuestionsModal />
					<Modal
						onRequestClose={closeRegLayout}
						open={layoutOpen}
						closeable={false}
						cancellable
						title="Registration Layout"
						footer={(
							<>
								<button onClick={closeRegLayout}>Cancel</button>
								<button className="primary" onClick={handleSaveRegistrationLayout}>Save</button>
							</>
						)}
					>
						<div className="layout-modal-body">
							<input type="radio" name="layout" value="form-left" id="form-left" checked={_layout === "form-left"} onChange={() => set_Layout("form-left")} />
							<label htmlFor="form-left"><img src={formLeft} alt="Registration page with the form on the left" /></label>
							<input type="radio" name="layout" value="form-right" id="form-right" checked={_layout === "form-right"} onChange={() => set_Layout("form-right")} />
							<label htmlFor="form-right"><img src={formRight} alt="Registration page with the form on the right" /></label>
						</div>
					</Modal>
					<RegistrationPreviewModal open={previewMode === EPreviewTypes.Registration} />
				</div>
			</>}
		</div>
	);
}